import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FilterChips } from '~/components/FilterChips';
import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import { FilterDrawer } from '~/modules/settings/components/FilterDrawer';
import { LABEL_TYPES } from '~/modules/settings/constants/Filter';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import { useSort } from '~/modules/settings/hooks/useSort';
import type { Label, LabelListItem } from '~settings/api/lib/schemas';
import { HeaderSmall } from '~settings/components/HeaderSmall';
import { ListControls } from '~settings/components/ListControls';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { UserDetailFormFilter } from '~settings/modules/user/components/UserDetailFormFilter';
import { translateOptions } from '~settings/utils/helpers';

import { useUserDetail } from '../../hooks/useUserDetail';
import { useUserLabelsColumns } from '../../hooks/useUserLabelsColumns';

const emptyLabels: LabelListItem[] = [];

export function UserLabels() {
  const columns = useUserLabelsColumns();
  const { t } = useTranslation('settings');
  const { id, isInternal, rolesByResource } = useUserDetail();

  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const { sorting, setSorting, sortParams } = useSort();
  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters();
  const { searchTerm } = useSearch();
  const { labelList, isFetching } = useLabelList({
    skip,
    take,
    userId: id,
    ...(searchTerm && { search: searchTerm }),
    ...sortParams,
    ...filterData,
  });

  const labels = useMemo(() => {
    if (!labelList?.data) {
      return emptyLabels;
    }

    return labelList.data.map((label) => ({
      ...label,
      roles: rolesByResource.get(`${label.id}-LABEL`) ?? [],
    }));
  }, [labelList?.data, rolesByResource]);

  const navigate = useNavigate();
  const onClick = (row: Label) => {
    navigate(SETTINGS_ROUTES.LABEL_DETAIL(row.id));
  };

  return (
    <>
      <HeaderSmall className="mb-0">{t('labels')}</HeaderSmall>
      <ListControls
        placeholder={t('searchLabels')}
        page={page}
        total={labelList?.total ?? 0}
        perPage={perPage}
        entity="labels"
        {...(!isInternal && { onFilterClick: openDrawer })}
        internalFilter={
          <FilterChips
            options={translateOptions(LABEL_TYPES, t)}
            path="labelType"
          />
        }
        setPageNumber={setPageNumber}
      />

      <Table
        data={labels}
        columns={columns}
        rowClassName="odd:bg-level-03"
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{ isLoading: isFetching, prefillRowsNumber: 10 }}
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <UserDetailFormFilter open={drawerOpen} onApply={closeDrawer} />
      </FilterDrawer>
    </>
  );
}

UserLabels.displayName = 'UserLabels';
